import { Link } from '@tanstack/react-router';
import {
  DashboardDetailsPageSkeleton,
  DashboardDetailsTab,
  DashboardDetailsTabList,
  DashboardDetailsHeader,
  dashboardPageTitleClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Tabs, TabsContent } from '@shigo-ui/ui';
import { ArrowLeft, Pencil } from 'lucide-react';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useNetworkQuery } from '../../hooks';
import { NetworkDashboard } from './NetworkDashboard';

export function NetworkDetailsPage({ networkId }: { networkId: string }) {
  const { can } = useShigoPermissions();
  const network = useNetworkQuery(networkId);
  const canEdit = can(ShigoPermission.NetworkEdit);

  if (network.isLoading) {
    return (
      <DashboardDetailsPageSkeleton
        label={dashboardT('networkDashboard.loading')}
        showHeaderAction={canEdit}
        tabCount={1}
        contentRowCount={5}
      />
    );
  }
  if (network.isError || !network.data) {
    return (
      <main className="page-gutter min-w-0 py-6">
        <div className="border-border bg-card space-y-4 rounded-md border p-6">
          <h1 className={dashboardPageTitleClassName()}>
            {dashboardT('networkDashboard.detailsError')}
          </h1>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void network.refetch()}>
              {dashboardT('common.retry')}
            </Button>
            <Button asChild variant="outline">
              <Link to="/networks">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {dashboardT('networkDashboard.back')}
              </Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-w-0 overflow-x-clip">
      <Tabs value="overview">
        <div className="border-border bg-card/60 border-b">
          <section className="page-gutter mx-auto flex w-full max-w-[1920px] flex-col gap-4 pt-5">
            <DashboardDetailsHeader
              back={
                <ReturnBackLink
                  fallback={{ to: '/networks' }}
                  label={dashboardT('networkDashboard.back')}
                />
              }
              title={
                <h1 className={dashboardPageTitleClassName('truncate')}>{network.data.name}</h1>
              }
              primaryAction={
                canEdit ? (
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/networks/edit/$networkId" params={{ networkId }}>
                      <Pencil className="size-4" aria-hidden="true" />
                      {dashboardT('networks.edit')}
                    </Link>
                  </Button>
                ) : undefined
              }
            />
            <DashboardDetailsTabList>
              <DashboardDetailsTab value="overview">
                {dashboardT('networkDashboard.overview')}
              </DashboardDetailsTab>
            </DashboardDetailsTabList>
          </section>
        </div>
        <section className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 py-6">
          <TabsContent value="overview" className="mt-0 min-w-0">
            <NetworkDashboard networkId={networkId} />
          </TabsContent>
        </section>
      </Tabs>
    </main>
  );
}
